// R10: distance-weighted carving with displaced sand. Repeated passages deepen
// the groove, while the real height field also keeps low ridges pushed to the
// sides and a small leading pile that is reworked as the ball continues forward.
export const SAND_TRAIL_REVISION = 'displaced-sand-ridges-r10';
export const TRAIL_STAMP_SPACING = .019;
export const TRAIL_MAX_DEPTH = .32;
export const TRAIL_MAX_RIM = .068;
export const TRAIL_CUT_PER_METER = .42;
const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));
const validWidth = v => Number.isFinite(v) && v > 0 ? clamp(v,.1,4) : 1;
export function trailWidthForRadius(radius, restRadius, restWidth = 1) {
 const base = validWidth(restWidth);
 if (!Number.isFinite(radius) || !Number.isFinite(restRadius) || radius <= 0 || restRadius <= 0) return base;
 return base * radius / restRadius;
}
export function sampleTrailSegment(from, to, stamp) {
 if (![from.x, from.z, to.x, to.z].every(Number.isFinite)) return 0;
 const start=validWidth(from.width),end=validWidth(to.width),dx=to.x-from.x,dz=to.z-from.z,length=Math.hypot(dx,dz);
 if(length<1e-7)return 0;
 const dirX=dx/length,dirZ=dz/length,steps=Math.max(1,Math.ceil(length/TRAIL_STAMP_SPACING)),travel=length/steps;
 for(let i=0;i<steps;i++){
  const t=(i+.5)/steps; // midpoint quadrature avoids frame-end over-carving
  stamp(from.x+dx*t,from.z+dz*t,start+(end-start)*t,travel,dirX,dirZ,t);
 }
 return steps;
}
export function composeTrailHeight(cut,rim){
 const safeCut=Number.isFinite(cut)?Math.min(0,cut):0,safeRim=Number.isFinite(rim)?Math.max(0,rim):0;
 return safeCut+safeRim*clamp(1+safeCut*10,0.06,1);
}
export function carveTrailStamp(field,x,z,width=1,travel=TRAIL_STAMP_SPACING,dirX=1,dirZ=0){
 if(!Number.isFinite(x)||!Number.isFinite(z)||!Number.isFinite(travel)||travel<=0)return null;
 const {n,size,cell,heights,cuts,rims,surface,minSurface,maxSurface,maxDepth=TRAIL_MAX_DEPTH}=field;
 width=validWidth(width);let dl=Math.hypot(dirX,dirZ);if(!Number.isFinite(dl)||dl<1e-6){dirX=1;dirZ=0;dl=1;}dirX/=dl;dirZ/=dl;
 const radius=.43*width,cx=(x/size+.5)*(n-1),cz=(z/size+.5)*(n-1),cr=Math.ceil(radius/cell);
 const bounds={minX:Math.max(0,Math.floor(cx)-cr-1),minZ:Math.max(0,Math.floor(cz)-cr-1),maxX:Math.min(n-1,Math.ceil(cx)+cr+1),maxZ:Math.min(n-1,Math.ceil(cz)+cr+1)};
 if(bounds.minX>bounds.maxX||bounds.minZ>bounds.maxZ)return null;
 for(let iz=Math.max(0,Math.floor(cz)-cr);iz<=Math.min(n-1,Math.ceil(cz)+cr);iz++)for(let ix=Math.max(0,Math.floor(cx)-cr);ix<=Math.min(n-1,Math.ceil(cx)+cr);ix++){
  const ox=(ix-cx)*cell,oz=(iz-cz)*cell,d=Math.hypot(ox,oz)/width;if(d>.43)continue;
  const i=iz*n+ix,previous=heights[i],forward=(ox*dirX+oz*dirZ)/width,side=(-ox*dirZ+oz*dirX)/width;
  let cutKernel=0,dose=0;
  if(d<=.33){cutKernel=Math.pow(Math.max(0,1-(d/.23)**2),1.65);dose=TRAIL_CUT_PER_METER*travel/width*cutKernel;
   cuts[i]=-clamp(maxDepth-(maxDepth+cuts[i])*Math.exp(-dose/maxDepth),0,maxDepth);
   // A later pass can flatten an old ridge before cutting through it.
   if(cutKernel>.03)rims[i]*=Math.exp(-dose*12);
  }
  // Sand displaced by the groove collects on both shoulders. A smaller mound
  // sits just in front of the ball; later stamps naturally carve through it,
  // so it follows the moving end of the trail rather than making a centre rail.
  const sideKernel=Math.exp(-Math.pow((Math.abs(side)-.315)/.050,2))*Math.exp(-Math.pow(Math.abs(forward)/.37,4));
  const frontKernel=Math.exp(-Math.pow((forward-.305)/.060,2)-Math.pow(side/.18,2));
  const ridgeDose=(sideKernel*.18+frontKernel*.105)*travel/width;
  if(ridgeDose>1e-7)rims[i]=TRAIL_MAX_RIM-(TRAIL_MAX_RIM-rims[i])*Math.exp(-ridgeDose/TRAIL_MAX_RIM);
  const ridgeSurvival=clamp(1+cuts[i]*10,0.06,1);
  heights[i]=cuts[i]+rims[i]*ridgeSurvival;
  surface[i]=clamp(surface[i]+heights[i]-previous,minSurface,maxSurface);
 }
 return bounds;
}
// Signed 16-bit encoding keeps deep grooves and the low displaced ridges.
// Legacy R7/R8 data can still be loaded without erasure.
export function encodeTrailHeights(heights){
 const bytes=new Uint8Array(heights.length*2),v=new DataView(bytes.buffer);
 for(let i=0;i<heights.length;i++)v.setInt16(i*2,Math.round(clamp(Number.isFinite(heights[i])?heights[i]:0,-TRAIL_MAX_DEPTH,.08)*10000),true);
 let result='';for(let i=0;i<bytes.length;i+=8192)result+=String.fromCharCode(...bytes.subarray(i,i+8192));
 return btoa(result);
}
export function decodeTrailHeights(data,count){
 const result=new Float32Array(count);
 if(data.h16){const raw=atob(data.h16);if(raw.length!==count*2)throw new Error('Invalid trail save size');
  const bytes=Uint8Array.from(raw,c=>c.charCodeAt(0)),view=new DataView(bytes.buffer);
  for(let i=0;i<count;i++)result[i]=clamp(view.getInt16(i*2,true)/10000,-TRAIL_MAX_DEPTH,.08);
 }else if(data.h){const raw=atob(data.h);if(raw.length!==count)throw new Error('Invalid legacy trail save size');
  for(let i=0;i<count;i++)result[i]=clamp((raw.charCodeAt(i)-180)/1400,-.12,.04);
 }
 return result;
}
